import React, { useRef, useEffect, useMemo } from 'react';
import { message } from 'antd';
import { useTranslation } from 'react-i18next';

import BlocklyGame from '../../blocks/core/component/BlocklyGame/BlocklyGame';
import BlocklyDraw from '../../blocks/core/component/BlocklyDraw/BlocklyDraw';
import BlocklyArcade from '../../blocks/core/component/BlocklyArcade/BlocklyArcade';
import ContentViewer from '../../components/editor/ContentViewer';
import QuestionViewer from '../../components/forms/QuestionViewer';
import H5PLessonPlayer from '../../components/H5PLesson/student/H5PLessonPlayer';

import { TLesson } from '../../models/courses/lesson';
import { TCodification } from '../../models/referential/referential';

import { useReferential } from '../../context/ReferentialContext';

import {
    CODIFICATION_LESSON_METADATA,
    CODIFICATION_LESSON_TYPE,
    CODIFICATION_QUESTION_TYPE,
    CODIFICATION_TYPE,
} from '../../constant/codification';

import {
    TLessonCharactersTOTCharacters,
    TLessonCharacterTOTCharacter,
} from '../../utils/blockly/character';
import { TLessonObstaclesTOObstacles } from '../../utils/blockly/obstacle';
import {
    TLessonTargetsTOTTargets,
    TLessonTargetTOTTarget,
} from '../../utils/blockly/target';
import { TLessonSceneTOScene } from '../../utils/blockly/scene';
import { TLessonLinesTOTLines } from '../../utils/blockly/line';
import { TLessonBlocklyContentTOBlocklyBlocks } from '../../utils/blockly/blocklyfields';
import { stringToBool } from '../../utils/format/valueFormat';
import FillInTheBlank from '../../components/forms/FillInTheBlank';
import DragAndDrop from '../../components/forms/DragAndDrop';
import IDEPhaserStudent from '../../components/IDEPhaser/IDEPhaserStudent';
import IDEPhaserTeacher from '../../components/IDEPhaser/IDEPhaserTeacher';
import HtmlCssStudent from '../../components/HtmlCss/HtmlCssStudent';
import LessonBlocklyPhaser from '../../components/BlocklyPhaser/root/student/LessonBlocklyPhaser';
import LessonBlocklyPhaserTeacher from '../../components/BlocklyPhaser/root/teacher/LessonBlocklyPhaserTeacher';
import IDEConsoleStudent from '../../components/IDEConsole/IDEConsoleStudent';
import { TH5PLessonConfig } from '../../models/courses/h5p';
import { TAIDatasetLessonConfig, createEmptyAIDatasetLessonConfig } from '../../models/courses/aiDataset';
import AIDatasetLessonPlayer from '../../components/AIDatasetLesson/student/AIDatasetLessonPlayer';
import AIDatasetLessonTeacher from '../../components/AIDatasetLesson/teacher/AIDatasetLessonTeacher';
import {
    TAIModelBuilderLessonConfig,
    createEmptyAIModelBuilderLessonConfig,
} from '../../models/courses/aiModelBuilder';
import AIModelBuilderLessonPlayer from '../../components/AIModelBuilderLesson/student/AIModelBuilderLessonPlayer';
import AIModelBuilderLessonTeacher from '../../components/AIModelBuilderLesson/teacher/AIModelBuilderLessonTeacher';
import {
    TAIPlayGroundLessonConfig,
    createEmptyAIPlayGroundLessonConfig,
} from '../../models/courses/aiPlayGround';
import AIPlayGroundLessonPlayer from '../../components/AIPlayGroundLesson/student/AIPlayGroundLessonPlayer';
import AIPlayGroundLessonTeacher from '../../components/AIPlayGroundLesson/teacher/AIPlayGroundLessonTeacher';
import { useTilmiAuth } from '../../hooks/useTilmiAuth';
import { CODIFICATION_TYPE_USERS } from '../../constant/codification';
import {
    TAILessonPromptingConfig,
    createEmptyAILessonPromptingConfig,
} from '../../models/courses/aiLessonPrompting';
import AILessonPromptingStudent from '../../components/AILessonPrompting/student/AILessonPromptingStudent';
import AILessonPromptingTeacher from '../../components/AILessonPrompting/teacher/AILessonPromptingTeacher';
import { TPuzzleLessonConfig, createEmptyPuzzleLessonConfig } from '../../models/courses/puzzleLesson';
import PuzzleLessonStudent from '../../components/PuzzleLesson/student/PuzzleLessonStudent';
import PuzzleLessonTeacher from '../../components/PuzzleLesson/teacher/PuzzleLessonTeacher';
import { normalizeGraphicDesignLessonConfig } from '../../models/courses/graphicDesign';
import LoadingBox from '../../components/loading/LoadingBox';
import { lazyWithRetry } from '../../utils/lazyWithRetry';
import AppErrorBoundary from '../../components/errors/AppErrorBoundary';
import AssociationLessonStudent from '../../components/AssociationLesson/student/AssociationLessonStudent';
import AssociationLessonTeacher from '../../components/AssociationLesson/teacher/AssociationLessonTeacher';
import { createEmptyAssociationConfig } from '../../components/AssociationLesson/types';

// lazyWithRetry : un chunk disparu après un déploiement recharge la page une fois.
const GraphicDesignLessonStudent = lazyWithRetry(() =>
    import('../../components/GraphicDesignLesson/student/GraphicDesignLessonStudent'));
const GraphicDesignLessonTeacher = lazyWithRetry(() =>
    import('../../components/GraphicDesignLesson/teacher/GraphicDesignLessonTeacher'));

interface LessonContentProps {
    lesson: TLesson | null;
    checkResult: (isValid: boolean) => void;
}

const LessonContent: React.FC<LessonContentProps> = ({ lesson, checkResult }) => {
    const { t, i18n } = useTranslation();
    const {
        getCodificationIdByCodes,
        getCodificationCodeById,
        getCodificationsByCodeType,
    } = useReferential();
    const { getCodeTypeUser } = useTilmiAuth();
    const isTeacher = getCodeTypeUser() === CODIFICATION_TYPE_USERS.TEACHER;

    // Initialiser les hooks au niveau supérieur
    const xmlRef = useRef<string>('');
    const [codificationType, setCodificationType] = React.useState<TCodification | undefined>(undefined);

    // Définir la fonction isValid
    const isValid = (valid: boolean) => {
        checkResult(valid);
        if (valid) {
            message.success(t('message.bravo'));
        }
    };



    // Définir les fonctions utilitaires
    const getMetadataValue = React.useCallback(
        (keyCode: string): string | undefined => {
            if (!lesson) return undefined;
            const keyId = getCodificationIdByCodes(
                CODIFICATION_TYPE.LESSON_METADATA,
                keyCode
            );
            return lesson.metadata?.find((e) => e.key_id === keyId)?.value;
        },
        [lesson, getCodificationIdByCodes]
    );

    const getMetadataBoolean = (keyCode: string): boolean => {
        const value = getMetadataValue(keyCode);
        return stringToBool(value) || false;
    };

    // Utiliser useEffect pour mettre à jour codificationType
    useEffect(() => {
        if (lesson) {
            const lessonTypes: TCodification[] = getCodificationsByCodeType(
                CODIFICATION_TYPE.LESSON_TYPE
            );
            const codification = lessonTypes.find(
                (e) => e.id === lesson.lesson_type_id
            );
            setCodificationType(codification);

            xmlRef.current =
                getMetadataValue(CODIFICATION_LESSON_METADATA.BLOCKLY_XML_TOOLBOX) || '';
        } else {
            setCodificationType(undefined);
        }
        // eslint-disable-next-line
    }, [lesson]);

    // Utiliser useEffect pour valider automatiquement l'exercice
    useEffect(() => {
        if (codificationType?.code === CODIFICATION_LESSON_TYPE.LESSON_SLIDE) {
            checkResult(true);
        }
        // eslint-disable-next-line
    }, [codificationType?.code]);



    const platformLanguage = useMemo(
        () => (i18n.language || 'fr').split('-')[0],
        [i18n.language]
    );

    const slideContent = useMemo(() => {
        if (!lesson?.slide) {
            return { html: '', css: '' };
        }

        const withFallback = (primary?: string | null, fallback?: string | null) => {
            const primaryTrimmed = (primary ?? '').trim();
            if (primaryTrimmed.length > 0) return primaryTrimmed;
            return (fallback ?? '').trim();
        };

        switch (platformLanguage) {
            case 'ar':
                return {
                    html: withFallback(lesson.slide.html_ar, lesson.slide.html),
                    css: withFallback(lesson.slide.css_ar, lesson.slide.css),
                };
            case 'en':
                return {
                    html: withFallback(lesson.slide.html_en, lesson.slide.html),
                    css: withFallback(lesson.slide.css_en, lesson.slide.css),
                };
            default:
                return {
                    html: (lesson.slide.html ?? '').trim(),
                    css: (lesson.slide.css ?? '').trim(),
                };
        }
    }, [lesson?.slide, platformLanguage]);

    // Retours anticipés après les hooks
    if (!lesson) {
        return <p>{t('text.exercideUnavailable')}</p>;
    }

    if (!codificationType) {
        return <p>{t('text.exercideUnavailable')}</p>;
    }

    // Données communes pour les différents types de leçons
    const toolboxContents = TLessonBlocklyContentTOBlocklyBlocks(
        lesson.blocklycontent,
        getCodificationCodeById
    );
    const initialBlocksJsonOrXML = getMetadataValue(
        CODIFICATION_LESSON_METADATA.BLOCKLY_XML_TOOLBOX
    );
    const scene = TLessonSceneTOScene(lesson.scene);

    // Rendu en fonction du type de leçon
    switch (codificationType.code) {
        case CODIFICATION_LESSON_TYPE.PSEUDO_CODE:
            // Teachers see teacherCode, students see initialCode
            return isTeacher ? (
                <IDEPhaserTeacher
                    lesson={lesson}
                    language="javascript"
                />
            ) : (
                <IDEPhaserStudent
                    lesson={lesson}
                    checkResult={isValid}
                    language="javascript"
                />
            );

        case CODIFICATION_LESSON_TYPE.HTML_CSS:
            return (
                <HtmlCssStudent
                    lesson={lesson}
                    checkResult={isValid}
                    statement={lesson.statement}
                />
            );


        case CODIFICATION_LESSON_TYPE.PYTHON_PHASER:
            // Teachers see teacherCode, students see initialCode
            return isTeacher ? (
                <IDEPhaserTeacher
                    lesson={lesson}
                    language="python"
                />
            ) : (
                <IDEPhaserStudent
                    lesson={lesson}
                    checkResult={isValid}
                    language="python"
                />
            );

        case CODIFICATION_LESSON_TYPE.BLOCKLY_PHASER:
            // Teachers see teacherWorkspace, students see workspace
            return isTeacher ? (
                <LessonBlocklyPhaserTeacher
                    lesson={lesson}
                />
            ) : (
                <LessonBlocklyPhaser
                    lesson={lesson}
                    checkResult={isValid}
                />
            );
        case CODIFICATION_LESSON_TYPE.BLOCKLY_GAME:
            return (
                <BlocklyGame
                    toolbox={{ contents: toolboxContents }}
                    initialBlocksJsonOrXML={initialBlocksJsonOrXML}
                    character={TLessonCharacterTOTCharacter(
                        lesson.characters?.[0],
                        getCodificationIdByCodes
                    )}
                    obstacles={TLessonObstaclesTOObstacles(lesson.obstacles)}
                    targets={TLessonTargetsTOTTargets(
                        lesson.targets,
                        getCodificationIdByCodes
                    )}
                    scene={scene}
                    xmlRef={xmlRef}
                    showGrid={getMetadataBoolean(CODIFICATION_LESSON_METADATA.SHOW_GRID)}
                    drawResult={getMetadataBoolean(
                        CODIFICATION_LESSON_METADATA.DRAW_RESULT
                    )}
                    autoReset={getMetadataBoolean(
                        CODIFICATION_LESSON_METADATA.AUTO_RESET
                    )}
                    autoReach={getMetadataBoolean(
                        CODIFICATION_LESSON_METADATA.AUTO_REACH
                    )}
                    respectOrder={getMetadataBoolean(
                        CODIFICATION_LESSON_METADATA.RESPECT_ORDER
                    )}
                    mustReachAllTargets={getMetadataBoolean(
                        CODIFICATION_LESSON_METADATA.REACH_ALL_TARGET
                    )}
                    checkResult={isValid}
                />
            );

        case CODIFICATION_LESSON_TYPE.BLOCKLY_DRAW:
            return (
                <BlocklyDraw
                    toolbox={{ contents: toolboxContents }}
                    initialBlocksJsonOrXML={initialBlocksJsonOrXML}
                    character={TLessonCharacterTOTCharacter(
                        lesson.characters?.[0],
                        getCodificationIdByCodes
                    )}
                    targets={TLessonLinesTOTLines(lesson.lines)}
                    scene={scene}
                    xmlRef={xmlRef}
                    autoReset={getMetadataBoolean(
                        CODIFICATION_LESSON_METADATA.AUTO_RESET
                    )}
                    canChangeXY={getMetadataBoolean(
                        CODIFICATION_LESSON_METADATA.CAN_CHANGE_XY
                    )}
                    checkResult={isValid}
                />
            );

        case CODIFICATION_LESSON_TYPE.BLOCKLY_ARCADE:
            return (
                <BlocklyArcade
                    toolbox={{ contents: toolboxContents }}
                    initialBlocksJsonOrXML={initialBlocksJsonOrXML}
                    characters={TLessonCharactersTOTCharacters(
                        lesson.characters,
                        getCodificationIdByCodes
                    )}
                    target={TLessonTargetTOTTarget(
                        lesson.targets?.[0],
                        getCodificationIdByCodes
                    )}
                    scene={scene}
                    xmlRef={xmlRef}
                    checkResult={isValid}
                    editionMode={false}
                />
            );

        case CODIFICATION_LESSON_TYPE.LESSON_SLIDE:
            return (
                <ContentViewer
                    html={slideContent.html}
                    css={slideContent.css}
                />
            );

        case CODIFICATION_LESSON_TYPE.CONSOLE_JS:
            return (
                <IDEConsoleStudent
                    lesson={lesson}
                    checkResult={isValid}
                    language="javascript"
                />
            );

        case CODIFICATION_LESSON_TYPE.LESSON_H5P: {
            const config = (lesson.config?.config as TH5PLessonConfig | undefined) ?? {
                videoUrl: '',
                allowSkipping: false,
                quizzes: [],
            };
            return (
                <H5PLessonPlayer
                    config={config}
                    onComplete={() => isValid(true)}
                />
            );
        }

        case CODIFICATION_LESSON_TYPE.LESSON_AI_DATASET: {
            const rawConfig = lesson.config?.config as TAIDatasetLessonConfig | undefined;
            const config = rawConfig?.dataset
                ? rawConfig
                : createEmptyAIDatasetLessonConfig();

            return isTeacher ? (
                <AIDatasetLessonTeacher
                    config={config}
                    statement={lesson.statement}
                />
            ) : (
                <AIDatasetLessonPlayer
                    config={config}
                    onComplete={() => isValid(true)}
                    statement={lesson.statement}
                    lessonId={lesson.id}
                />
            );
        }

        case CODIFICATION_LESSON_TYPE.LESSON_AI_MODEL_BUILDER: {
            const rawConfig = lesson.config?.config as TAIModelBuilderLessonConfig | undefined;
            const config = rawConfig?.dataset
                ? rawConfig
                : createEmptyAIModelBuilderLessonConfig();

            return isTeacher ? (
                <AIModelBuilderLessonTeacher
                    config={config}
                    statement={lesson.statement || config.statement}
                />
            ) : (
                <AIModelBuilderLessonPlayer
                    config={config}
                    onComplete={() => isValid(true)}
                    statement={lesson.statement || config.statement}
                    lessonId={lesson.id}
                />
            );
        }
        case CODIFICATION_LESSON_TYPE.LESSON_AI_PLAY_GROUND: {
            const rawConfig = lesson.config?.config as TAIPlayGroundLessonConfig | undefined;
            const config = rawConfig?.dataset
                ? rawConfig
                : createEmptyAIPlayGroundLessonConfig();

            return isTeacher ? (
                <AIPlayGroundLessonTeacher
                    config={config}
                    statement={lesson.statement || config.statement}
                />
            ) : (
                <AIPlayGroundLessonPlayer
                    config={config}
                    onComplete={() => isValid(true)}
                    statement={lesson.statement || config.statement}
                    lessonId={lesson.id}
                />
            );
        }

        case CODIFICATION_LESSON_TYPE.LESSON_AI_PROPMTING: {
            const rawConfig = lesson.config?.config as TAILessonPromptingConfig | undefined;
            const config = rawConfig?.renderType
                ? rawConfig
                : createEmptyAILessonPromptingConfig();

            return isTeacher ? (
                <AILessonPromptingTeacher
                    config={config}
                    statement={lesson.statement || config.statement}
                />
            ) : (
                <AILessonPromptingStudent
                    config={config}
                    onComplete={() => isValid(true)}
                    statement={lesson.statement || config.statement}
                    lessonId={lesson.id}
                />
            );
        }

        case CODIFICATION_LESSON_TYPE.LESSON_PUZZLE: {
            const rawConfig = lesson.config?.config as TPuzzleLessonConfig | undefined;
            const config = rawConfig?.components
                ? { ...createEmptyPuzzleLessonConfig(), ...rawConfig }
                : createEmptyPuzzleLessonConfig();

            return isTeacher ? (
                <PuzzleLessonTeacher
                    config={config}
                    statement={lesson.statement || config.statement}
                />
            ) : (
                <PuzzleLessonStudent
                    config={config}
                    onComplete={() => isValid(true)}
                    statement={lesson.statement || config.statement}
                    lessonId={lesson.id}
                />
            );
        }

        case CODIFICATION_LESSON_TYPE.LESSON_ASSOCIATION: {
            const rawConfig = lesson.config?.config as any;
            const config = rawConfig?.left ? { ...createEmptyAssociationConfig(), ...rawConfig } : createEmptyAssociationConfig();
            return isTeacher ? (
                <AssociationLessonTeacher
                    config={config}
                    statement={lesson.statement || config.statement}
                />
            ) : (
                <AssociationLessonStudent
                    config={config}
                    statement={lesson.statement || config.statement}
                    onComplete={() => isValid(true)}
                />
            );
        }

        case CODIFICATION_LESSON_TYPE.LESSON_GRAPHIC_DESIGN: {
            const config = normalizeGraphicDesignLessonConfig(lesson.config?.config);
            // Frontière locale : une erreur dans la leçon laisse la navigation utilisable.
            return <AppErrorBoundary resetKey={lesson.id}>
                <React.Suspense fallback={<LoadingBox />}>
                    {isTeacher ? <GraphicDesignLessonTeacher config={config}
                        statement={lesson.statement || config.statement} />
                        : <GraphicDesignLessonStudent config={config}
                            statement={lesson.statement || config.statement}
                            lessonId={lesson.id} onComplete={() => isValid(true)} />}
                </React.Suspense>
            </AppErrorBoundary>;
        }

        case CODIFICATION_LESSON_TYPE.CONSOLE_PY:
            return (
                <IDEConsoleStudent
                    lesson={lesson}
                    checkResult={isValid}
                    language="python"
                />
            );

        case CODIFICATION_LESSON_TYPE.LESSON_FORMS:
            if (!lesson.question) {
                return <p>Pas de questions disponibles.</p>;
            }

            const questionType = getCodificationsByCodeType(CODIFICATION_TYPE.QUESTION_TYPE)?.find(
                (e) => e.id === lesson.question?.question_type_id
            );

            if (!questionType) {
                return <p>Type de question non supporté.</p>;
            }

            if (questionType.code === CODIFICATION_QUESTION_TYPE.FILL_IN_THE_BLANKS) {
                return (
                    <FillInTheBlank
                        question={lesson.question}
                        onNextLesson={() => {
                            isValid(true);
                        }}
                    />
                );
            }
            else if (questionType.code === CODIFICATION_QUESTION_TYPE.DRAG_AND_DROP) {
                return (
                    <DragAndDrop
                        question={lesson.question}
                        onNextLesson={() => {
                            isValid(true);
                        }}
                    />
                );
            }

            return (
                <QuestionViewer
                    question={lesson.question}
                    onNextLesson={() => {
                        isValid(true);
                    }}
                />
            );

        default:
            return <p>{t('text.exercideUnavailable')}</p>;
    }
};

export default React.memo(LessonContent);
